import { Wallet } from "@api/wallet-screener/types.ts";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api, Api } from "@api/base-api-request.ts";
import { ApiSupportedChain, WalletType } from "@providers/wallet";
import { useCallback } from "react";

const QUERY_KEY = "wallet-screener";
export const WALLET_SCREENER_LIMIT = 100;

export type WalletSortColumn =
  | "invested"
  | "trades"
  | "realizedPnl"
  | "realizedPnlPercent"
  | "unrealizedPnl"
  | "unrealizedPnlPercent"
  | "netPnl"
  | "netPnlPercent"
  | "peakPnl"
  | "peakPnlPercent"
  | "winRate";

export type WalletScreenerParams = {
  cursor?: string;
  limit?: number;
  timeFrame?: number;
  searchWallet?: string | null;
  isUserWallet?: boolean;
  filters?: {
    invested?: {
      min?: number | null;
      max?: number | null;
    };
    trades?: {
      min?: number;
      max?: number;
    };
    realizedPnl?: {
      min?: number | null;
      max?: number | null;
    };
    realizedPnlPercent?: {
      min?: number | null;
      max?: number | null;
    };
    unrealizedPnl?: {
      min?: number | null;
      max?: number | null;
    };
    unrealizedPnlPercent?: {
      min?: number | null;
      max?: number | null;
    };
    netPnl?: {
      min?: number | null;
      max?: number | null;
    };
    netPnlPercent?: {
      min?: number | null;
      max?: number | null;
    };
    peakPnl?: {
      min?: number | null;
      max?: number | null;
    };
    peakPnlPercent?: {
      min?: number | null;
      max?: number | null;
    };
    winRate?: {
      min?: number | null;
      max?: number | null;
    };
    sort: {
      column: WalletSortColumn;
      direction: string;
    };
  };
};

const transformParams = (params: WalletScreenerParams) => {
  const result: Record<string, string | number> = {};

  if (params.cursor != null) {
    result.cursor = params.cursor;
  }

  if (params.limit != null) {
    result.limit = params.limit;
  }

  if (params.isUserWallet != null) {
    result.isUserWallet = params.isUserWallet ? "true" : "false";
  }

  if (params.timeFrame != null) {
    result.timeFrame = params.timeFrame;
  }

  if (params.searchWallet != null) {
    result.searchWallet = params.searchWallet;
  }

  if (params.filters) {
    // Clean up filters by removing null values
    const cleanFilters = Object.entries(params.filters).reduce(
      (acc, [key, value]) => {
        // Skip null values
        if (value === null) return acc;

        // Handle nested objects (min/max ranges and exchange/sort objects)
        if (typeof value === "object" && value !== null) {
          // Handle sort object
          if ("column" in value && "direction" in value) {
            acc[key] = {
              column: value.column,
              direction: value.direction,
            };
          }

          // For min/max range objects
          if ("min" in value || "max" in value) {
            const rangeObj = Object.entries(value).reduce(
              (rangeAcc, [rangeKey, rangeVal]) => {
                if (rangeVal !== null) {
                  rangeAcc[rangeKey] = rangeVal;
                }
                return rangeAcc;
              },
              {} as Record<string, number | null>,
            );

            // Only add the range object if it has properties
            if (Object.keys(rangeObj).length > 0) {
              acc[key] = rangeObj;
            }
          }
        }
        // Handle primitive values
        else {
          acc[key] = value;
        }

        return acc;
      },
      {} as Record<
        string,
        | string
        | number
        | Record<string, number | null>
        | { column: string; direction: string }
        | null
      >,
    );

    // Only add filters if there are any after cleaning
    if (Object.keys(cleanFilters).length > 0) {
      result.filters = JSON.stringify(cleanFilters);
    }
  }

  return result;
};

type RawWalletResponse = {
  cursor?: number;
  count: number;
  items: {
    [address: string]: {
      chain: string;
      chainSymbol: string;
      realized_pnl_usd: string;
      realized_roi_percentage: string;
      unrealized_pnl_usd: string;
      unrealized_roi_percentage: string;
      combined_pnl_usd: string;
      combined_roi_percentage: string;
      total_buy_usd: string;
      tokens_traded: number;
      winrate: number;
    };
  };
};

const fetchWalletScreenerData = async (
  api: Api,
  params: WalletScreenerParams,
): Promise<RawWalletResponse> => {
  return api
    .get(`walletDiscovery`, { searchParams: transformParams(params) })
    .json();
};

const toWalletArray = (rawData: RawWalletResponse): Wallet[] => {
  return Object.entries(rawData.items).map(([address, walletData]) => ({
    address,
    chain: walletData.chain === "1" ? WalletType.ETH : WalletType.SOL,
    chainSymbol:
      walletData.chain === "1" ? ApiSupportedChain.ETH : ApiSupportedChain.SOL,
    isFavourite: false,
    invested: parseFloat(walletData.total_buy_usd),
    trades: walletData.tokens_traded,
    realizedPNL: {
      amount: parseFloat(walletData.realized_pnl_usd),
      percentage: parseFloat(walletData.realized_roi_percentage),
    },
    unrealizedPNL: {
      amount: parseFloat(walletData.unrealized_pnl_usd),
      percentage: parseFloat(walletData.unrealized_roi_percentage),
    },
    netPNL: {
      amount: parseFloat(walletData.combined_pnl_usd),
      percentage: parseFloat(walletData.combined_roi_percentage),
    },
    peakPNL: {
      amount: 0,
      percentage: 0,
    },
    winRate: walletData.winrate,
  }));
};

const defaultSelector = (rawData: RawWalletResponse): Wallet[] => {
  return toWalletArray(rawData);
};

const useGetInfiniteWalletScreener = (params: WalletScreenerParams) => {
  return useInfiniteQuery<
    RawWalletResponse,
    Error,
    Wallet[],
    [string, WalletScreenerParams],
    string | undefined
  >({
    queryKey: [QUERY_KEY, params],
    queryFn: async ({ pageParam = undefined }) =>
      fetchWalletScreenerData(api, {
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => {
      return lastPage?.count > 0 ? lastPage?.cursor?.toString() : undefined;
    },
    initialPageParam: undefined,
    select: (data) => {
      return [...data.pages.flatMap((page) => toWalletArray(page))];
    },
  });
};

const useGetWalletScreener = <TData = Wallet[]>(
  params: WalletScreenerParams,
  selector: (data: RawWalletResponse) => TData = defaultSelector as (
    data: RawWalletResponse,
  ) => TData,
) =>
  useQuery<RawWalletResponse, Error, TData>({
    queryKey: [QUERY_KEY, params],
    queryFn: async () => fetchWalletScreenerData(api, params),
    select: useCallback(selector, [selector]),
  });

export {
  fetchWalletScreenerData,
  useGetWalletScreener,
  useGetInfiniteWalletScreener,
  QUERY_KEY as WALLET_SCREENER_KEY,
};
